import axios from 'utilities/axios';
import { CancelToken, isCancel } from 'utilities/axios';
import { useMutation, useQuery } from 'react-query';
import queryClient from 'utilities/queryClient';
import { AUDIENCE_TYPE } from 'constants/common';
import { useIntl } from 'react-intl';
import ErrorHandler from 'utilities/errorHandler';
import { t } from 'utilities/lang';

const API = {
  DEMOGRAPHIC: 'demographic',
  POLITICAL: 'political',
  AUDIENCES: 'audiences',
  AUDIENCES_FILTER: 'audiences/filter',
  ACTIVE_AUDIENCE: (id) => `audiences/${id}/de-active`,
  CREATE_PERSONAL_DATA: 'audiences/personal-data',
  UPSELL_PERSONAL_DATA: 'audiences/personal-data/upsell',
  VALIDATE_CSV_FILE: 'audiences/validate-csv-file',
  VALIDATE_AUDIENCE_NAME: 'audiences/validate-audience-name',
  GENERATE_PIXEL: 'audiences/generate-pixel',
  VERIFY_LANDING_URL: 'audiences/verify-pixel',
  CREATE_RETARGETING_AUDIENCE: 'audiences/re-targeting',
  CLONE: (id) => `audiences/${id}/clone`,
  UPDATE_AUDIENCE: (id) => `audiences/${id}`,
  BEHAVIORAL_CONSUMERS: 'common/behavioral-consumers',
  BEHAVIORAL_CATEGORIES: 'common/behavioral-categories',
  ESTIMATED_COUNT: 'audiences/estimated-count',
  CUSTOM_AUDIENCE: 'audiences/custom-audience',
  CUSTOM_AUDIENCE_DETAIL: (id) => `audiences/custom-audience/${id}`,
  PREVIEW_AUDIENCE: 'audiences/preview',
};

export const useGetAudienceList = (filters) => {
  return useQuery(
    ['audiences', filters],
    () =>
      axios
        .get(API.AUDIENCES_FILTER, {
          params: {
            ...filters,
            isActive: !filters.isActive
              ? undefined
              : filters.isActive === 1
              ? true
              : false,
          },
        })
        .then((res) => res.data),
    { keepPreviousData: true, enabled: !!filters.page }
  );
};

export const useActiveAudience = () => {
  return useMutation(
    (value) => axios.put(API.ACTIVE_AUDIENCE(value.id), value),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('audiences');
      },
    }
  );
};

const addDemographicToObj = (obj, list, index, parentIds = []) => {
  if (index === list.length || !list[index]) return;
  const element = list[index];
  obj[element.id] = { ...element, parentIds };
  if (element.items?.length) {
    addDemographicToObj(obj, element.items, 0, [...parentIds, element.id]);
  }
  addDemographicToObj(obj, list, index + 1, parentIds);
};

export const getAudienceTranslation = (name, parentName, keyPrefix, intl) => {
  const containsOnlyLetters = (str) => /[A-Za-z]/.test(str);
  const tokenize = (str) =>
    str
      .replace(/\s+([A-Za-z])/g, (_, ch) => ch.toUpperCase())
      .replace(/[^A-Za-z0-9]/g, '');

  if (!containsOnlyLetters(name)) return name;

  const baseKey = `audience.${keyPrefix}.${tokenize(parentName || '')}`;
  const fullKey = parentName
    ? `${baseKey}.${tokenize(name)}`
    : `${baseKey}${tokenize(name)}`;

  return intl.formatMessage({ id: fullKey, defaultMessage: name });
};

export const useGetDemographicList = () => {
  const intl = useIntl();
  return useQuery(
    'demographic',
    () =>
      axios.get(API.DEMOGRAPHIC).then((res) => {
        const translatedData = res.data?.map((parent) => ({
          ...parent,
          items: parent.items?.map((sub) => ({
            ...sub,
            name: getAudienceTranslation(
              sub.name,
              sub.parentName,
              'demographic',
              intl
            ),
          })),
          name: getAudienceTranslation(parent.name, null, 'demographic', intl),
        }));

        const demographicObj = {};
        if (res.data?.length) {
          addDemographicToObj(demographicObj, translatedData, 0);
        }
        return {
          listDemographic: translatedData,
          demographicObj,
        };
      }),
    {
      initialData: {
        listDemographic: [],
        demographicObj: {},
      },
    }
  );
};

export const useGetPoliticalList = () => {
  const intl = useIntl();
  return useQuery(
    'political',
    () =>
      axios.get(API.POLITICAL).then((res) => {
        const translatedData = res.data?.map((parent) => ({
          ...parent,
          items: parent.items?.map((sub) => ({
            ...sub,
            name: getAudienceTranslation(
              sub.name,
              sub.parentName,
              'political',
              intl
            ),
          })),
          name: getAudienceTranslation(parent.name, null, 'political', intl),
        }));

        const demographicObj = {};
        if (res.data?.length) {
          addDemographicToObj(demographicObj, translatedData, 0);
        }
        return {
          listDemographic: translatedData,
          demographicObj,
        };
      }),
    {
      initialData: {
        listDemographic: [],
        demographicObj: {},
      },
    }
  );
};

export const useCreateAudience = () => {
  return useMutation((values) => axios.post(API.AUDIENCES, values));
};

export const useUpSellPersonalDataAudience = () => {
  return useMutation((values) => {
    const formData = new FormData();
    formData.append('file', values.file);
    formData.append('name', values.name);
    formData.append('audienceId', values.audienceId);
    formData.append('type', values.type);
    formData.append('paymentId', values.paymentId);
    formData.append('discountCode', values.discountCode);
    if (values.advertiserId != null) {
      formData.append('advertiserId', values.advertiserId);
    }

    return axios.post(API.UPSELL_PERSONAL_DATA, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  });
};

export const useCreateAudiencePersonalData = () => {
  return useMutation(
    (values) => {
      const formData = new FormData();
      formData.append('file', values.targetCsv?.originFileObj);
      formData.append('name', values.name);
      formData.append('tibAdvertiserId', values.tibAdvertiserId);
      if (values.advertiserId != null) {
        formData.append('advertiserId', values.advertiserId);
      }

      return axios.post(API.CREATE_PERSONAL_DATA, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    {
      onError: () => {},
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries('profile');
        queryClient.invalidateQueries([
          'audiences',
          {
            type: AUDIENCE_TYPE.PERSONAL_DATA,
            advertiserId: variables.advertiserId,
          },
        ]);
      },
    }
  );
};

export const useCreateRetargetingAudience = () => {
  return useMutation((values) =>
    axios.post(API.CREATE_RETARGETING_AUDIENCE, values)
  );
};

export const useGetAudience = (id) => {
  return useQuery(
    ['audience', id],
    () => axios.get(`${API.AUDIENCES}/${id}`).then((res) => res.data),
    { enabled: !!id, initialData: {} }
  );
};

export const useUpdateAudience = () => {
  return useMutation((values) => {
    const formData = new FormData();
    [
      'id',
      'type',
      'name',
      'fileId',
      'customAges',
      'genders',
      'demographicIds',
      'politicalIds',
      'pixelId',
      'landingPageURL',
      'landingUrl',
      'advertiserId',
      'includes',
      'narrows',
      'excludes',
    ].map((el) => {
      if (values[el]) {
        if (Array.isArray(values[el])) {
          return values[el].map((item) => formData.append(el, item));
        }

        formData.append(el, values[el]);
      }
    });

    if (values.targetCsv) {
      formData.append('file', values.targetCsv.originFileObj);
    }

    return axios.put(`${API.AUDIENCES}/${values.id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  });
};

export const useValidateCSVFile = () => {
  const fetchApi = (csvFile) => {
    const formData = new FormData();
    formData.append('file', csvFile);
    return axios.post(API.VALIDATE_CSV_FILE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };
  return useMutation((file) => fetchApi(file), { onError: () => {} });
};

export const useValidateAudienceName = () => {
  return useMutation(
    (values) =>
      axios.post(
        `${API.VALIDATE_AUDIENCE_NAME}?id=${values.id}&audienceName=${values.name}`
      ),
    {
      onError: () => {},
    }
  );
};

export const useGeneratePixel = () => {
  return useMutation((values) =>
    axios.post(API.GENERATE_PIXEL, values).then((res) => res.data)
  );
};

export const useVerifyLandingUrl = () => {
  return useMutation((values) =>
    axios.post(API.VERIFY_LANDING_URL, values).then((res) => res.data)
  );
};

export const useCloneAudience = () => {
  return useMutation(
    (values) => axios.post(API.CLONE(values.audienceId), values),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('audiences');
      },
    }
  );
};

export const useDeleteAudience = () => {
  return useMutation((id) => axios.delete(API.UPDATE_AUDIENCE(id)), {
    onSuccess: () => {
      queryClient.invalidateQueries('audiences');
    },
  });
};

export const useGetAllAudienceByType = (type, advertiserId) => {
  return useQuery(
    ['audiences', { type, advertiserId }],
    () =>
      axios
        .get(API.AUDIENCES, {
          params: { type, advertiserId },
        })
        .then((res) => res.data),
    { enabled: !!type }
  );
};

export const useGetBehavioralConsumerData = () => {
  return useQuery(
    ['behavioral-consumers'],
    () => axios.get(API.BEHAVIORAL_CONSUMERS).then((res) => res.data),
    { staleTime: 10 * 60 * 1000, retry: false }
  );
};

export const useGetBehavioralCategories = () => {
  return useQuery(
    ['behavioral-categories'],
    () => axios.get(API.BEHAVIORAL_CATEGORIES).then((res) => res.data),
    { staleTime: 10 * 60 * 1000, retry: false }
  );
};

export const useGetEstimatedAudienceCount = (payload, options = {}) => {
  return useQuery(
    ['estimated-audience-count', payload],
    ({ signal }) => {
      const source = CancelToken.source();
      if (signal) {
        signal.addEventListener('abort', () =>
          source.cancel('Request superseded')
        );
      }
      return axios
        .post(API.ESTIMATED_COUNT, payload, { cancelToken: source.token })
        .then((res) => {
          const data = res.data;
          // BE returns { audienceCount: number } — extract the count
          if (data && typeof data === 'object' && 'audienceCount' in data) {
            return data.audienceCount;
          }
          return data;
        })
        .catch((err) => {
          if (isCancel(err)) return;
          throw err;
        });
    },
    {
      enabled: !!payload,
      ...options,
      onError: (error) => {
        ErrorHandler.showError(error, { title: t("error.title.AudienceCalculationError") });
      },
    }
  );
};

export const useCreateTunnlCustomAudience = () => {
  return useMutation((values) => axios.put(API.CUSTOM_AUDIENCE, values), {
    onSuccess: () => {
      queryClient.invalidateQueries('audiences');
    },
  });
};

// Recursively appends a nested JS object to FormData using ASP.NET Core dot-notation
// e.g. { a: { b: [{ c: 1 }] } } → 'prefix.a.b[0].c' = '1'
const appendNestedToFormData = (formData, obj, prefix) => {
  if (obj === null || obj === undefined) return;
  if (Array.isArray(obj)) {
    obj.forEach((item, i) => appendNestedToFormData(formData, item, `${prefix}[${i}]`));
  } else if (typeof obj === 'object') {
    Object.entries(obj).forEach(([k, v]) => {
      appendNestedToFormData(formData, v, prefix ? `${prefix}.${k}` : k);
    });
  } else {
    formData.append(prefix, obj !== null && obj !== undefined ? String(obj) : '');
  }
};

export const useUpdateTunnlCustomAudience = (id) => {
  return useMutation((values) => {
    const formData = new FormData();
    if (values.name) formData.append('name', values.name);
    if (values.advertiserId) formData.append('advertiserId', values.advertiserId);
    formData.append('type', 'CustomTargeting');
    if (values.customAudienceModel) {
      appendNestedToFormData(formData, values.customAudienceModel, 'customAudienceSegmentModel');
    }
    return axios.put(API.UPDATE_AUDIENCE(id), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries('audiences');
      // Remove detail cache so component re-mounts fresh with correct data on next load
      queryClient.removeQueries(['tunnl-custom-audience', id]);
    },
  });
};

export const useGetTunnlCustomAudienceDetail = (id, options = {}) => {
  return useQuery(
    ['tunnl-custom-audience', id],
    () => axios.get(API.CUSTOM_AUDIENCE_DETAIL(id)).then((res) => res.data),
    { enabled: !!id, ...options }
  );
};

// Maps API snake_case group names → display names used by PreviewAudienceModal translation logic
const PREVIEW_GROUP_NAME_MAP = {
  age_range: 'Age',
  education_level: 'Education',
  ethnicity: 'Ethnicity',
  income_level: 'Income',
  children_in_household: 'Children In Household',
  geography_classification: 'Community Type',
  marital_status: 'Marital Status',
  party: 'Party',
  registered: 'Registered',
  sex: 'Gender',
};

export const useGetPreviewAudienceData = (payload, options = {}) => {
  return useQuery(
    ['previewAudience', payload],
    () =>
      axios.post(API.PREVIEW_AUDIENCE, payload).then((res) => {
        const raw = res.data;
        // API uses camelCase (ASP.NET Core default serializer).
        // Transform flat audiencePreview array → grouped demographics structure expected by PreviewAudienceModal.
        // API item: { group (snake_case), key, count, percent }
        // FE expects: [{ group (display name), categories: [{ name, count, percentage }] }]
        const groupMap = {};
        (raw.audiencePreview || []).forEach((item) => {
          const displayGroup = PREVIEW_GROUP_NAME_MAP[item.group] || item.group;
          if (!groupMap[displayGroup]) {
            groupMap[displayGroup] = { group: displayGroup, categories: [] };
          }
          groupMap[displayGroup].categories.push({
            name: item.key,
            count: item.count,
            percentage: item.percent,
          });
        });
        // Remap countMetadata camelCase → snake_case keys expected by PreviewAudienceModal
        const cm = raw.countMetadata || {};
        return {
          demographics: Object.values(groupMap),
          count_metadata: {
            audience_count: cm.audienceCount,
            geography_count: cm.geographyCount,
            percent_of_geography: cm.percentOfGeography,
            unique_cellphone_count: cm.uniqueCellphoneCount,
            unique_household_count: cm.uniqueHouseholdCount,
          },
          geography_breakdown: raw.geographyBreakdown,
        };
      }),
    {
      enabled: !!payload,
      retry: false,
      ...options,
      onError: (error) => {
        ErrorHandler.showError(error, { title: t("error.title.AudienceCalculationError") });
      },
    }
  );
};
